import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAdminToken } from '../../../admin/sessionSlice';
import { ROOM_OBJECT_IDS } from '../data/gameText';
import {
  startEscapeGame,
  saveEscapeProgress,
  completeEscapeGame,
  fetchEscapeResult,
} from '../services/escapeApi';
import { sendReport, extractReportIdFromResponse } from '../../../functions/sendReport';

const USER_DATA_KEY = 'userData';

export const REPORT_OK_STATUSES = new Set(['added', 'updated', 'skipped_demo']);
const REPORT_RETRYABLE_STATUS = 'in_progress_elsewhere';
const REPORT_SETTLE_RETRY_COUNT = 4;
const REPORT_SETTLE_RETRY_DELAY_MS = 1200;

function delay(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function settleCompletionReport(
  runComplete,
  { retries = REPORT_SETTLE_RETRY_COUNT, delayMs = REPORT_SETTLE_RETRY_DELAY_MS } = {}
) {
  let lastResult = null;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    const result = await runComplete();
    lastResult = result;
    if (!result?.reportStatus || result.reportStatus !== REPORT_RETRYABLE_STATUS) {
      return result;
    }
    if (attempt < retries) {
      await delay(delayMs);
    }
  }
  return lastResult;
}

function readStoredUserData() {
  try {
    return JSON.parse(sessionStorage.getItem(USER_DATA_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

function readUserId() {
  const stored = readStoredUserData();
  return stored.userId || stored.userid || stored.id || '';
}

function readReportFields() {
  const stored = readStoredUserData();
  return {
    gameId: stored.gameId || null,
    name: stored.name || '',
    role: stored.role || stored.source || '',
    employeeId: stored.employeeId || null,
    reportToken: stored.token || '',
    round: 'Room to Escape',
  };
}

function readStoredReportId() {
  const stored = readStoredUserData();
  return stored.reportId || stored.report_id || null;
}

function persistStoredReportId(reportId) {
  if (!reportId) return;
  try {
    const stored = readStoredUserData();
    sessionStorage.setItem(USER_DATA_KEY, JSON.stringify({ ...stored, reportId }));
  } catch {
    /* ignore */
  }
}

function formatReportTime(totalSeconds) {
  const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
  const hh = Math.floor(seconds / 3600);
  const mm = Math.floor((seconds % 3600) / 60);
  const ss = seconds % 60;
  if (hh > 0) {
    return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  }
  return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
}

function stateToInitial(state) {
  const completed = Array.isArray(state.completedActivities) ? state.completedActivities : [];
  const currentIdx = ROOM_OBJECT_IDS.indexOf(state.currentActivity);
  const unlockedIndex = currentIdx === -1 ? ROOM_OBJECT_IDS.length - 1 : currentIdx;
  return {
    unlockedIndex,
    completed,
    activityScores: state.activityScores || {},
    elapsedSeconds: Number(state.elapsedSeconds) || 0,
    isResumed: completed.length > 0 || Number(state.elapsedSeconds) > 0,
  };
}

export function useEscapePersistence() {
  const adminToken = useSelector(selectAdminToken);
  const userIdRef = useRef(readUserId());
  const userId = userIdRef.current;
  const reportFieldsRef = useRef(readReportFields());
  const reportIdRef = useRef(readStoredReportId());

  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [gameRunId, setGameRunId] = useState(null);
  const [initialState, setInitialState] = useState(null);
  const [completedResult, setCompletedResult] = useState(null);

  const syncFrontendReport = useCallback(
    async ({ totalScore = 0, elapsedSeconds = 0 } = {}) => {
      const [sessionId = '', organizationId = ''] = (adminToken || '').split('&');
      const reportFields = reportFieldsRef.current;
      const role = reportFields.role || '';

      if (!userId) return reportIdRef.current || null;
      if (role === 'demobypass' || role === 'DEMO') return reportIdRef.current || null;

      try {
        const response = await sendReport({
          sessionId,
          organizationId,
          userId,
          role,
          token: reportFields.reportToken,
          gameId: reportFields.gameId,
          name: reportFields.name || '',
          round: reportFields.round,
          points: Math.max(0, Number(totalScore) || 0),
          time: formatReportTime(elapsedSeconds),
          reportId: reportIdRef.current || undefined,
        });
        const nextReportId = extractReportIdFromResponse(response) || reportIdRef.current || null;
        if (nextReportId && nextReportId !== reportIdRef.current) {
          reportIdRef.current = nextReportId;
          persistStoredReportId(nextReportId);
        }
        return nextReportId;
      } catch (err) {
        console.error('Escape frontend sendReport failed:', err);
        return reportIdRef.current || null;
      }
    },
    [adminToken, userId]
  );

  const bootstrapAndStart = useCallback(async () => {
    if (!adminToken || !userId) {
      setStatus('error');
      setError('Missing player session. Please return to the platform and relaunch this game.');
      return;
    }

    setStatus('loading');
    setError(null);

    try {
      const startRes = await startEscapeGame({
        token: adminToken,
        userId,
        employeeId: reportFieldsRef.current.employeeId,
        reportToken: reportFieldsRef.current.reportToken,
        gameId: reportFieldsRef.current.gameId,
        name: reportFieldsRef.current.name,
        role: reportFieldsRef.current.role,
        reportId: reportIdRef.current || undefined,
        round: reportFieldsRef.current.round,
      });
      let state = startRes.state;

      const allActivitiesDone = ROOM_OBJECT_IDS.every((id) =>
        (state.completedActivities || []).includes(id)
      );
      if (state.status === 'in_progress' && allActivitiesDone) {
        const completeRes = await settleCompletionReport(() =>
          completeEscapeGame({
            token: adminToken,
            userId,
            gameRunId: state.gameRunId,
            elapsedSeconds: state.elapsedSeconds,
            reportId: reportIdRef.current || undefined,
            ...reportFieldsRef.current,
          })
        );
        state = completeRes.state;

        if (state?.status === 'completed') {
          await syncFrontendReport({
            totalScore: state.totalScore,
            elapsedSeconds: state.elapsedSeconds,
          });
          setGameRunId(state.gameRunId);
          setCompletedResult({
            totalScore: state.totalScore,
            elapsedSeconds: state.elapsedSeconds,
          });
          setStatus('completed');
          return;
        }

        if (completeRes.reportStatus && !REPORT_OK_STATUSES.has(completeRes.reportStatus)) {
          setError('Your game was saved, but we could not finalize completion. Please retry.');
          setStatus('error');
          return;
        }
      }

      if (state.status === 'completed') {
        setGameRunId(state.gameRunId);
        setCompletedResult({ totalScore: state.totalScore, elapsedSeconds: state.elapsedSeconds });
        setStatus('completed');
        return;
      }

      setGameRunId(state.gameRunId);
      setInitialState(stateToInitial(state));
      setStatus('ready');
    } catch (err) {
      setError(err.message || 'Could not connect to the server.');
      setStatus('error');
    }
  }, [adminToken, userId, syncFrontendReport]);

  useEffect(() => {
    bootstrapAndStart();
  }, [bootstrapAndStart]);

  const sendProgress = useCallback(
    (payload, options) =>
      saveEscapeProgress(
        {
          token: adminToken,
          userId,
          gameRunId,
          activityId: payload.activityId,
          activityScore: payload.activityScore,
          elapsedSeconds: payload.elapsedSeconds,
          gameId: reportFieldsRef.current.gameId,
          name: reportFieldsRef.current.name,
          role: reportFieldsRef.current.role,
          employeeId: reportFieldsRef.current.employeeId,
          reportToken: reportFieldsRef.current.reportToken,
          reportId: reportIdRef.current || undefined,
          round: reportFieldsRef.current.round,
        },
        options
      ),
    [adminToken, userId, gameRunId]
  );

  const pendingActivitiesRef = useRef([]);

  const drainPendingActivities = useCallback(
    async (latestElapsedSeconds, latestTotalScore, options) => {
      while (pendingActivitiesRef.current.length > 0) {
        const next = pendingActivitiesRef.current[0];
        try {
          await syncFrontendReport({
            totalScore: latestTotalScore,
            elapsedSeconds: latestElapsedSeconds,
          });
          await sendProgress(
            {
              activityId: next.activityId,
              activityScore: next.activityScore,
              elapsedSeconds: latestElapsedSeconds,
            },
            options
          );
          pendingActivitiesRef.current.shift();
        } catch (err) {
          setSaveError(err.message || 'Progress could not be saved.');
          return false;
        }
      }
      return true;
    },
    [sendProgress, syncFrontendReport]
  );

  const saveProgress = useCallback(
    async ({ activityId, activityScore, elapsedSeconds, totalScore } = {}, { keepalive = false } = {}) => {
      if (!gameRunId) return null;

      const drained = await drainPendingActivities(elapsedSeconds, totalScore, { keepalive });
      if (!drained) {
        if (activityId != null) {
          pendingActivitiesRef.current.push({ activityId, activityScore });
        }
        return null;
      }

      try {
        await syncFrontendReport({ totalScore, elapsedSeconds });
        const res = await sendProgress({ activityId, activityScore, elapsedSeconds }, { keepalive });
        setSaveError(null);
        return res;
      } catch (err) {
        if (activityId != null) {
          pendingActivitiesRef.current.push({ activityId, activityScore });
        }
        setSaveError(err.message || 'Progress could not be saved.');
        return null;
      }
    },
    [drainPendingActivities, sendProgress, syncFrontendReport, gameRunId]
  );

  const completeGame = useCallback(
    async ({ elapsedSeconds, totalScore } = {}) => {
      if (!gameRunId) return null;

      const drained = await drainPendingActivities(elapsedSeconds, totalScore, {});
      if (!drained) return null;

      try {
        const res = await settleCompletionReport(() =>
          completeEscapeGame({
            token: adminToken,
            userId,
            gameRunId,
            elapsedSeconds,
            reportId: reportIdRef.current || undefined,
            ...reportFieldsRef.current,
          })
        );
        if (res?.success && res?.state?.status === 'completed') {
          await syncFrontendReport({
            totalScore: res.state.totalScore,
            elapsedSeconds: res.state.elapsedSeconds,
          });
        }
        setSaveError(null);
        return res;
      } catch (err) {
        setSaveError(err.message || 'Could not save your final result.');
        return null;
      }
    },
    [adminToken, userId, gameRunId, drainPendingActivities, syncFrontendReport]
  );

  const loadResult = useCallback(async () => {
    if (!adminToken || !userId) return null;
    return fetchEscapeResult({ token: adminToken, userId });
  }, [adminToken, userId]);

  return {
    status,
    error,
    saveError,
    gameRunId,
    initialState,
    completedResult,
    saveProgress,
    completeGame,
    loadResult,
    retry: bootstrapAndStart,
  };
}

export { readUserId };
